import {
  BellRing,
  Command,
  Fingerprint,
  KeyRound,
  Languages,
  Layers,
  Lock,
  Palette,
  Shield,
  ShieldCheck,
  Users,
  WalletCards,
  Webhook,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Navigate, useSearchParams } from "react-router-dom";

import { canReachPayoutBatches } from "@/app/auth/canReachPayoutBatches";
import { useNavContext } from "@/app/auth/useNavContext";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useIsCryptomateOrg } from "@/modules/dashboard/cards/hooks/useIsCryptomateOrg";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { availableDashboardAlertTypes } from "@/shared/dashboard-alerts/dashboardAlertAvailability";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { ApiKeysAccessSection } from "../components/access/ApiKeysAccessSection";
import { PoliciesSection } from "../components/access/PoliciesSection";
import { RolesSection } from "../components/access/RolesSection";
import { SimulatorSection } from "../components/access/SimulatorSection";
import { UsuariosSection } from "../components/access/UsuariosSection";
import { AppearanceSection } from "../components/AppearanceSection";
import { CardWalletSection } from "../components/CardWalletSection";
import { DASHBOARD_ALERTS_SECTION_ID, DashboardAlertsSection } from "../components/DashboardAlertsSection";
import { DevicesSection } from "../components/DevicesSection";
import { LanguageSection } from "../components/LanguageSection";
import { PayoutBatchApprovalSection } from "../components/PayoutBatchApprovalSection";
import { SecuritySection } from "../components/SecuritySection";
import { VerificationSection } from "../components/VerificationSection";
import { WEBHOOKS_SECTION_ID, WebhooksSection } from "../components/webhooks/WebhooksSection";
import { CARD_WALLET_SECTION_ID } from "../constants/cardWalletSection";
import { PAYOUT_BATCH_APPROVAL_SECTION_ID } from "../constants/payoutBatchApprovalSection";
import { AccessDataProvider } from "../context/AccessDataProvider";
import { OrganizationWebhooksProvider } from "../context/OrganizationWebhooksProvider";
import { useApiKeysData, usePoliciesData, useRolesData, useUsersData } from "../hooks/useAccessData";
import { useOrganizationWebhooksData } from "../hooks/useOrganizationWebhooksData";

type LabelKey =
  | "appearance_section_title"
  | "language_section_title"
  | "dashboard_alerts_nav_label"
  | "security_section_title"
  | "devices_section_title"
  | "access_users_nav_label"
  | "access_apikeys_nav_label"
  | "access_roles_nav_label"
  | "access_policies_nav_label"
  | "access_sim_nav_label"
  | "webhooks_nav_label"
  | "verification_nav_label"
  | "card_wallet_nav_label"
  | "payout_batch_approval_nav_label";

type GroupKey =
  | "settings_group_preferences"
  | "settings_group_security"
  | "settings_group_developers"
  | "settings_group_access"
  | "settings_group_account";

interface SectionItem {
  id: string;
  labelKey: LabelKey;
  icon: LucideIcon;
  /**
   * Every section carries its group; the header renders whenever the group changes from the previous VISIBLE
   * section, so filtering sections out never drops a group heading.
   */
  group: GroupKey;
  /**
   * The body rendered in the main pane. It lives here, next to the nav entry, because the two used to be
   * separate lists that had to be edited in lockstep — one filtered the sub-nav, the other was hardcoded JSX,
   * and nothing forced them to agree. One list, one filter, no drift.
   */
  element: ReactNode;
}

const SECTIONS: SectionItem[] = [
  {
    id: "apariencia",
    labelKey: "appearance_section_title",
    icon: Palette,
    group: "settings_group_preferences",
    element: <AppearanceSection />,
  },
  {
    id: "idioma",
    labelKey: "language_section_title",
    icon: Languages,
    group: "settings_group_preferences",
    element: <LanguageSection />,
  },
  {
    // The corner alerts are a preference of this browser, like the theme: they sit with it, not with the
    // WhatsApp alerts each product configures for the whole organization.
    id: DASHBOARD_ALERTS_SECTION_ID,
    labelKey: "dashboard_alerts_nav_label",
    icon: BellRing,
    group: "settings_group_preferences",
    element: <DashboardAlertsSection />,
  },
  {
    id: "seguridad",
    labelKey: "security_section_title",
    icon: Lock,
    group: "settings_group_security",
    element: <SecuritySection />,
  },
  {
    id: "dispositivos",
    labelKey: "devices_section_title",
    icon: Fingerprint,
    group: "settings_group_security",
    element: <DevicesSection />,
  },
  {
    // The organization's own verification (KYC or KYB): status, submitted data, what it unlocks, and the way
    // to start or resume it. Organization members only — staff have no organization to verify.
    id: "verificacion",
    labelKey: "verification_nav_label",
    icon: ShieldCheck,
    group: "settings_group_account",
    element: <VerificationSection />,
  },
  {
    // The card wallet is a decision the organization takes once, not a preference per event: it lives with the
    // account. Organizations that dismissed the invite on the cards page come here to turn it on.
    id: CARD_WALLET_SECTION_ID,
    labelKey: "card_wallet_nav_label",
    icon: WalletCards,
    group: "settings_group_account",
    element: <CardWalletSection />,
  },
  {
    // Whether a batch needs a second person is the organization's rule for everyone in it, so it lives with the
    // account. Each approver may change it, behind their code.
    id: PAYOUT_BATCH_APPROVAL_SECTION_ID,
    labelKey: "payout_batch_approval_nav_label",
    icon: Layers,
    group: "settings_group_account",
    element: <PayoutBatchApprovalSection />,
  },
  {
    id: WEBHOOKS_SECTION_ID,
    labelKey: "webhooks_nav_label",
    icon: Webhook,
    group: "settings_group_developers",
    element: <WebhooksSection />,
  },
  {
    id: "usuarios",
    labelKey: "access_users_nav_label",
    icon: Users,
    group: "settings_group_access",
    element: <UsuariosSection />,
  },
  {
    id: "apikeys-acceso",
    labelKey: "access_apikeys_nav_label",
    icon: KeyRound,
    group: "settings_group_access",
    element: <ApiKeysAccessSection />,
  },
  {
    id: "roles",
    labelKey: "access_roles_nav_label",
    icon: Shield,
    group: "settings_group_access",
    element: <RolesSection />,
  },
  {
    id: "politicas",
    labelKey: "access_policies_nav_label",
    icon: Command,
    group: "settings_group_access",
    element: <PoliciesSection />,
  },
  {
    id: "simulador",
    labelKey: "access_sim_nav_label",
    icon: Zap,
    group: "settings_group_access",
    element: <SimulatorSection />,
  },
];

/** A section id missing from the map is visible; only an explicit `false` hides it. */
type SectionVisibility = Record<string, boolean>;

const SettingsPageContent = ({ visibility }: { visibility: SectionVisibility }) => {
  const { t } = useTranslations("settings");
  const { t: tGlobal } = useTranslations("global");

  const sections = useMemo(() => SECTIONS.filter((s) => visibility[s.id] !== false), [visibility]);

  const breadcrumb = useMemo(
    () => [{ label: tGlobal("sidebar_settings_label"), to: "/app/settings" }],
    [tGlobal],
  );
  useAppShellBreadcrumb(breadcrumb);

  const contentScrollRef = useRef<HTMLDivElement>(null);
  const [activeAnchor, setActiveAnchor] = useState<string>(SECTIONS[0].id);
  const [searchParams, setSearchParams] = useSearchParams();

  const goAnchor = useCallback(
    (id: string) => {
      setActiveAnchor(id);
      // Merge, never replace the whole query — `{ section: id }` dropped every other param — and only when
      // the URL actually disagrees. Both halves matter here because this also runs from the effect below:
      // every mount that already carried the right `?section=` (a bookmark, or the two-factor redirect this
      // same PR points at `?section=seguridad`) spent one guaranteed no-op `history.replaceState`, as did
      // every click on the section already open. The comparison has to happen out here — returning an
      // unchanged object from the updater form still navigates.
      if (searchParams.get("section") !== id) {
        const next = new URLSearchParams(searchParams);
        next.set("section", id);
        setSearchParams(next, { replace: true });
      }
      const container = contentScrollRef.current;
      const el = document.getElementById(`s-${id}`);
      if (container && el) {
        const top = el.offsetTop - container.offsetTop - 16;
        container.scrollTo({ top, behavior: "smooth" });
      } else if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    [searchParams, setSearchParams],
  );
  useEffect(() => {
    const section = searchParams.get("section");
    if (!section) return;
    const raf = requestAnimationFrame(() => goAnchor(section));
    return () => cancelAnimationFrame(raf);
  }, [searchParams, goAnchor]);

  // Scroll spy: watch our own right-pane scroll container, not the window. Tracks the FILTERED sections so a
  // hidden section can never become (or stay) the active one.
  useEffect(() => {
    const container = contentScrollRef.current;
    if (!container) return;

    const onScroll = () => {
      const containerTop = container.getBoundingClientRect().top;
      let current = sections[0].id;
      for (const s of sections) {
        const el = document.getElementById(`s-${s.id}`);
        if (!el) continue;
        const top = el.getBoundingClientRect().top - containerTop;
        if (top <= 96) current = s.id;
      }
      setActiveAnchor((prev) => (prev === current ? prev : current));
    };

    container.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => container.removeEventListener("scroll", onScroll);
  }, [sections]);

  return (
    // Two-pane layout: independent scroll on each pane. The aside doesn't
    // scroll because its content is short; the main content scrolls inside
    // its own region — so the sub-nav stays put no matter how far down the
    // user scrolls. We cancel ColocaAppShell's outer p-4 with -m-4 and fit
    // exactly its height so the parent's overflow-y-auto becomes inert.
    <div className="-m-4 flex h-full bg-background">
      {/* Sub-nav — lighter tone, fixed in its own pane */}
      <aside className="hidden w-[220px] shrink-0 overflow-y-auto border-r border-border bg-muted/30 px-4 py-6 lg:block">
        <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground/80">
          {t("settings_eyebrow_account")}
        </div>
        <h2 className="mb-5 text-base font-semibold text-foreground">{t("title_page")}</h2>
        <nav className="flex flex-col">
          {sections.map((s, i) => (
            <div key={s.id}>
              {(i === 0 || sections[i - 1].group !== s.group) && (
                <div
                  className={cn(
                    "px-2 pb-1.5 font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground/70",
                    i === 0 ? "pt-0" : "pt-4",
                  )}
                >
                  {t(s.group)}
                </div>
              )}
              <button
                type="button"
                onClick={() => goAnchor(s.id)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] transition-colors",
                  activeAnchor === s.id
                    ? "bg-foreground/5 font-semibold text-foreground"
                    : "text-muted-foreground hover:bg-foreground/5 hover:text-foreground",
                )}
              >
                <s.icon size={14} />
                {t(s.labelKey)}
              </button>
            </div>
          ))}
        </nav>
      </aside>

      {/* Main scroll pane */}
      <div ref={contentScrollRef} className="min-w-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-[940px] px-6 py-8 md:px-10">
          <header className="mb-7">
            <div className="mb-1.5 font-mono text-[11px] uppercase tracking-[0.06em] text-muted-foreground">
              {t("settings_eyebrow_account")}
            </div>
            <h1 className="text-[26px] font-semibold tracking-tight text-foreground">{t("title_page")}</h1>
            <div className="mt-1 text-sm text-muted-foreground">{t("settings_subtitle")}</div>
          </header>

          {/* Same filtered list that drives the sub-nav, so an anchor can never point at a body that is not
              rendered. Each section paints its own `s-<id>` anchor through `SettingsSection`. */}
          <div className="flex flex-col gap-[22px] pb-16">
            {sections.map((s) => (
              <Fragment key={s.id}>{s.element}</Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Organization members. The access sections are gated on the AUTHORITATIVE backend signal (a 403 surfaced as
 * `hasNoAccess`), not on the legacy `scopes[]` from `/auth/me` — the backend marks those deprecated and
 * authorizes purely by RBAC roles (deny-first), so a scope check could hide sections from users who do have
 * access through their roles (or show them to users who don't).
 */
const OrganizationSettings = () => {
  const { can, user } = useAuth();
  // Only crypto cards can go without the card wallet; on the other rail it is always on, so there is nothing to
  // decide. Without access to cards there is nothing to load or withdraw either.
  const decidesCardWallet = useIsCryptomateOrg() && can("cards:read");
  // A backend without self-approval sends no switch: the section would have nothing to read.
  const seesBatchApproval =
    canReachPayoutBatches(user) &&
    can("payout_batches:read") &&
    typeof user?.organization?.payout_batches_self_approval_enabled === "boolean";
  const navContext = useNavContext();
  // Without a page any alert is about, the section would list nothing.
  const getsDashboardAlerts = availableDashboardAlertTypes(navContext, { brebKeys: null }).length > 0;
  const { hasNoAccess: rolesNoAccess } = useRolesData();
  const { hasNoAccess: policiesNoAccess } = usePoliciesData();
  const { hasNoAccess: usersNoAccess } = useUsersData();
  const { hasNoAccess: keysNoAccess } = useApiKeysData();
  const { hasNoAccess: webhooksNoAccess } = useOrganizationWebhooksData();

  const visibility = useMemo<SectionVisibility>(
    () => ({
      // While a source is loading, `hasNoAccess` is false → the section shows optimistically and collapses
      // only when the backend confirms the 403 (menu-config style hiding, but backend-authoritative).
      [WEBHOOKS_SECTION_ID]: !webhooksNoAccess,
      [CARD_WALLET_SECTION_ID]: decidesCardWallet,
      [PAYOUT_BATCH_APPROVAL_SECTION_ID]: seesBatchApproval,
      [DASHBOARD_ALERTS_SECTION_ID]: getsDashboardAlerts,
      usuarios: !usersNoAccess,
      "apikeys-acceso": !keysNoAccess,
      roles: !rolesNoAccess,
      politicas: !policiesNoAccess,
      // The simulator needs the roles catalog to resolve anything, plus at least one principal source.
      simulador: !rolesNoAccess && !(usersNoAccess && keysNoAccess),
    }),
    [
      rolesNoAccess,
      policiesNoAccess,
      usersNoAccess,
      keysNoAccess,
      webhooksNoAccess,
      decidesCardWallet,
      seesBatchApproval,
      getsDashboardAlerts,
    ],
  );

  return <SettingsPageContent visibility={visibility} />;
};

/**
 * Coloca staff. Access administration is not part of an internal employee's own account: the catalog now has
 * its own surfaces — `/app/access/*` for roles, policies and API keys, `/app/users` for people.
 *
 * Only the SECTIONS are hidden. The route and its footer entry stay open on purpose: two-factor enrolment
 * lives in `SecuritySection` and nowhere else, and `DashboardLayout` sends every user here on the global
 * `auth:two-factor-required` event — closing the route would strand internal staff with no way to enrol.
 */
const INTERNAL_HIDDEN_SECTIONS: SectionVisibility = {
  verificacion: false,
  // Staff get no corner alerts: they would receive every organization's.
  [DASHBOARD_ALERTS_SECTION_ID]: false,
  // Staff switch a client's card wallet from `/app/organizations`; they have none of their own.
  [CARD_WALLET_SECTION_ID]: false,
  // Same for a client's batch approval rule; staff never approve their own batches anyway.
  [PAYOUT_BATCH_APPROVAL_SECTION_ID]: false,
  // Outbound webhooks belong to a client organization; the backend would demand an `organization_id`
  // from staff here, and a staff surface with an org selector is a later slice.
  [WEBHOOKS_SECTION_ID]: false,
  usuarios: false,
  "apikeys-acceso": false,
  roles: false,
  politicas: false,
  simulador: false,
};

/**
 * Where a staff member's old bookmark now lives. `?section=` is normally a scroll anchor, not a route, and
 * hijacking it would be wrong — but these anchors no longer exist for this reader, so the alternative is the
 * silence the page gives an unknown section: no scroll, no error, no explanation. `replace` keeps the back
 * button pointing at wherever they came from instead of bouncing between the two surfaces.
 *
 * Every hidden section that HAS somewhere to go is listed. `webhooks` and `simulador` are the ones that do
 * not: outbound webhooks belong to a client organization, and the access simulator has no equivalent outside
 * Settings. `tarjetas` has moved, but to a surface that is organization-scoped — see the member map below.
 */
const INTERNAL_SECTION_REDIRECTS: Record<string, string> = {
  usuarios: "/app/users",
  roles: "/app/access/roles",
  politicas: "/app/access/policies",
  "apikeys-acceso": "/app/access/api-keys",
};

/**
 * Sections that left Settings for the product they configure. Organization members only, because
 * `/app/cards/notifications` is scoped to the caller's own organization and would deny a staff member — for
 * whom this anchor keeps the page's usual silence rather than bouncing them into a `NoAccessView`.
 */
const MEMBER_SECTION_REDIRECTS: Record<string, string> = {
  tarjetas: "/app/cards/notifications",
};

export const SettingsPage = () => {
  const { isInternalUser } = useAuth();
  const [searchParams] = useSearchParams();

  if (isInternalUser) {
    const movedTo = INTERNAL_SECTION_REDIRECTS[searchParams.get("section") ?? ""];
    if (movedTo) return <Navigate to={movedTo} replace />;

    // Staff render without `AccessDataProvider`: every section it feeds is hidden for them, so mounting it
    // would fetch roles, policies, users and API keys for a page that shows none of them.
    return <SettingsPageContent visibility={INTERNAL_HIDDEN_SECTIONS} />;
  }

  const movedToProduct = MEMBER_SECTION_REDIRECTS[searchParams.get("section") ?? ""];
  if (movedToProduct) return <Navigate to={movedToProduct} replace />;

  // The access sections share one data source — a policy created in one is visible in the others without a
  // reload, and roles/policies/users are fetched once for all of them. The provider wraps the whole page so
  // the sub-nav can gate the access sections on the same authoritative data. It renders no DOM node.
  // `OrganizationWebhooksProvider` follows the same rule: one read of the endpoint, shared by the sub-nav
  // (which hides the section on a 403) and the section body.
  return (
    <AccessDataProvider>
      <OrganizationWebhooksProvider>
        <OrganizationSettings />
      </OrganizationWebhooksProvider>
    </AccessDataProvider>
  );
};
